/**
 * Validate form data for job creation
 * @param {Object} formData - Form data to validate
 * @param {boolean} isRecurring - Whether this is a recurring job
 * @returns {Object} Validation errors object
 */
export const validateJobForm = (formData, isRecurring = false) => {
  const errors = {};
  
  // Required fields validation
  if (!formData.propertyId) {
    errors.propertyId = 'Property is required';
  }
  
  if (!formData.scheduledDate) {
    errors.scheduledDate = 'Scheduled date is required';
  }
  
  if (!formData.serviceTypeId) {
    errors.serviceTypeId = 'Service Type is required';
  }

  // Recurring job specific validation
  if (isRecurring) {
    if (!formData.endDate) {
      errors.endDate = 'End date is required for recurring jobs';
    }
    
    // Validate end date is within 1 year and in the future
    if (formData.endDate) {
      const today = new Date();
      const selectedEndDate = new Date(formData.endDate);
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(today.getFullYear() + 1);
      
      if (selectedEndDate <= today) {
        errors.endDate = 'End date must be in the future';
      } else if (selectedEndDate > oneYearFromNow) {
        errors.endDate = 'End date cannot be more than 1 year from today';
      }
    }
    
    // Validate frequency-specific fields
    if (formData.frequency === 'WEEKLY' || formData.frequency === 'BIWEEKLY') {
      if (formData.dayOfWeek === undefined || formData.dayOfWeek === '') {
        errors.dayOfWeek = 'Day of week is required';
      }
    } else if (formData.frequency === 'MONTHLY') {
      if (!formData.dayOfMonth) {
        errors.dayOfMonth = 'Day of month is required for monthly jobs';
      }
    } else if (formData.frequency === 'QUARTERLY') {
      if (!formData.dayOfMonth) {
        errors.dayOfMonth = 'Day of month is required';
      }
    }
  }

  return errors;
};

/**
 * Validate financial field values
 * @param {string} payout - Payout value
 * @param {string} billedAmount - Billed amount value
 * @returns {Object} Financial validation errors
 */
export const validateFinancialFields = (payout, billedAmount) => {
  const errors = {};
  
  if (payout && (isNaN(parseFloat(payout)) || parseFloat(payout) < 0)) {
    errors.payout = 'Payout must be a valid positive number';
  }
  
  if (billedAmount && (isNaN(parseFloat(billedAmount)) || parseFloat(billedAmount) < 0)) {
    errors.billedAmount = 'Billed amount must be a valid positive number';
  }
  
  return errors;
};

/**
 * Validate time format
 * @param {string} timeString - Time string to validate
 * @returns {boolean} Whether time format is valid
 */
export const validateTimeFormat = (timeString) => {
  if (!timeString) return true; // Optional field
  
  const timeRegex = /^(0?[1-9]|1[0-2]):[0-5][0-9] (AM|PM|am|pm)$/;
  return timeRegex.test(timeString);
};

/**
 * Validate date is not in the past
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {boolean} Whether date is valid (today or future)
 */
export const validateDateNotInPast = (dateString) => {
  if (!dateString) return false;
  
  const selectedDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return selectedDate >= today;
};

/**
 * Check if there are any validation errors
 * @param {Object} errors - Errors object
 * @returns {boolean} Whether there are any errors
 */
export const hasValidationErrors = (errors) => {
  return Object.keys(errors).length > 0;
};

/**
 * Get first error message from errors object
 * @param {Object} errors - Errors object
 * @returns {string|null} First error message or null
 */
export const getFirstErrorMessage = (errors) => {
  const errorKeys = Object.keys(errors);
  if (errorKeys.length === 0) return null;
  
  return errors[errorKeys[0]];
}; 